<template>
    <div>
        <div
            class="afu-mb-6"
            v-if="$_meWorkExperience.length"
        >
            <C1Button
                variant="outline-secondary"
                :disabled="$_seekerFieldByCode('work_experience').read_only"
                @click="onAdd()"
            >
                <FontAwesomeIcon
                    :icon="['far', 'plus']"
                    class="afu-mr-2"
                />
                {{ $t('label.add_value', {value: $t('label.work_experience')}) }}
            </C1Button>
        </div>
        <div
            v-if="!$_meWorkExperience.length"
            class="af-no-result-found afu-text-black afu-text-sm afu-font-medium afu-text-center afu-mt-5 afu-p-7 afu-bg-white"
        >
            <div class="afu-text-neutral-dark afu-text-lg afu-mb-5 afu-flex afu-flex-col afu-gap-5 afu-justify-center afu-items-center">
                <svg
                    width="51"
                    height="50"
                    viewBox="0 0 51 50"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M25.5 0C11.6895 0 0.5 11.1895 0.5 25C0.5 38.8105 11.6895 50 25.5 50C39.3105 50 50.5 38.8105 50.5 25C50.5 11.1895 39.3105 0 25.5 0ZM25.5 45.1613C14.381 45.1613 5.33871 36.119 5.33871 25C5.33871 13.881 14.381 4.83871 25.5 4.83871C36.619 4.83871 45.6613 13.881 45.6613 25C45.6613 36.119 36.619 45.1613 25.5 45.1613ZM20.6613 20.1613C20.6613 18.377 19.2198 16.9355 17.4355 16.9355C15.6512 16.9355 14.2097 18.377 14.2097 20.1613C14.2097 21.9456 15.6512 23.3871 17.4355 23.3871C19.2198 23.3871 20.6613 21.9456 20.6613 20.1613ZM33.5645 16.9355C31.7802 16.9355 30.3387 18.377 30.3387 20.1613C30.3387 21.9456 31.7802 23.3871 33.5645 23.3871C35.3488 23.3871 36.7903 21.9456 36.7903 20.1613C36.7903 18.377 35.3488 16.9355 33.5645 16.9355ZM25.5 28.2258C21.9113 28.2258 16.5484 30.373 15.8427 34.3952C15.6411 35.5847 16.75 36.5625 17.9093 36.2198C21.0544 35.252 23.8972 34.6774 25.5 34.6774C27.1028 34.6774 29.9456 35.252 33.0907 36.2198C34.2399 36.5726 35.3589 35.5847 35.1573 34.3952C34.4516 30.373 29.0887 28.2258 25.5 28.2258Z"
                        fill="#E5E6E2"
                    />
                </svg>
                {{ $t('label.not_added_value', {value: $t('label.work_experience')}) }}
            </div>
            <C1Button
                variant="outline-secondary"
                :disabled="$_seekerFieldByCode('work_experience').read_only"
                @click="openModal()"
            >
                <FontAwesomeIcon
                    :icon="['far', 'plus']"
                    class="afu-mr-2"
                />
                {{ $t('label.add_value', {value: $t('label.work_experience')}) }}
            </C1Button>
        </div>

        <template v-else>
            <Card
                v-for="work in $_meWorkExperience"
                :key="work.id"
                class="af-work-experience-card afu-flex afu-flex-col md:afu-flex-row md:afu-items-center afu-justify-between"
            >
                <div>
                    <div class="af-work-experience-date afu-text-sm afu-text-neutral-dark afu-mb-3 afu-font-semibold">
                        {{ work.start_date | dateFormat('MMM YYYY') }}
                        <template v-if="(work.end_date || work.is_current) && work.start_date">
                            -
                        </template>

                        <template v-if="work.is_current">
                            CURRENT
                        </template>

                        <template v-else>
                            {{ work.end_date | dateFormat('MMM YYYY') }}
                        </template>
                    </div>

                    <div class="af-work-experience-employer afu-text-4xl afu-font-semibold afu-text-black afu-mb-1">
                        {{ work.employer_name }}
                    </div>

                    <div class="af-work-experience-title afu-text-base afu-text-secondary afu-font-semibold afu-mb-5">
                        {{ work.job_title }}
                    </div>

                    <div
                        v-if="getJobTypeName(work.jobType) || getContractTypeName(work.contractType) || getLocationName(work.location)"
                        class="af-work-experience-tags afu-flex afu-items-center afu-flex-wrap afu-gap-1"
                    >
                        <C1Tag
                            v-if="getJobTypeName(work.jobType)"
                            variant="outline-neutral"
                            size="sm"
                        >
                            {{ getJobTypeName(work.jobType) }}
                        </C1Tag>

                        <C1Tag
                            v-if="getContractTypeName(work.contractType)"
                            variant="outline-neutral"
                            size="sm"
                        >
                            {{ getContractTypeName(work.contractType) }}
                        </C1Tag>

                        <C1Tag
                            v-if="getLocationName(work.location)"
                            variant="outline-neutral"
                            size="sm"
                        >
                            {{ getLocationName(work.location) }}
                        </C1Tag>
                    </div>
                </div>

                <div class="af-work-experience-actions afu-mt-3 md:afu-mt-0">
                    <C1Button
                        @click="onEdit(work)"
                        :disabled="$_seekerFieldByCode('work_experience').read_only"
                        variant="outline-primary"
                        class="af-work-experience-btn-edit"
                    >
                        <FontAwesomeIcon
                            :icon="['far', 'edit']"
                            class="afu-mr-2"
                            fixed-width
                        />
                        {{ $t('label.edit') }}
                    </C1Button>

                    <C1Button
                        class="af-work-experience-btn-delete afu-ml-2"
                        variant="outline-primary"
                        :disabled="$_seekerFieldByCode('work_experience').read_only"
                        @click="onDestroy(work)"
                    >
                        <FontAwesomeIcon
                            :icon="['fal', 'trash-alt']"
                            class="afu-mr-2"
                            fixed-width
                        />
                        {{ $t('label.remove') }}
                    </C1Button>
                </div>
            </Card>
        </template>

        <WorkExperienceModal
            :is-modal-open="isModalOpen"
            v-if="isModalOpen"
            @closeModal="isModalOpen = false"
            :work-experience="selectedWorkExperience"
            :hide-description="hideDescription"
            @close="isModalOpen = false"
        />

        <WorkExperienceModalDestroy
            :is-modal-open="isModalDestroyOpen"
            v-if="isModalDestroyOpen"
            :work-experience="workExperienceToBeDestroyed"
            @close="isModalDestroyOpen = false"
            @closeModal="isModalDestroyOpen = false"
        />
    </div>
</template>

<script>
    import { mapState, mapGetters } from 'vuex';
    import has from 'lodash/has';
    import moment from 'moment/src/moment';
    import numeral from 'numeral';
    import WorkExperienceModal from '@/apps/dashboard/001/components/WorkExperience/WorkExperienceModal';
    import WorkExperienceModalDestroy from '@/apps/dashboard/001/components/WorkExperience/WorkExperienceModalDestroy';
    import C1Button from '@/components/C1Components/C1Button';
    import Card from '@/apps/dashboard/001/components/Card/Card';
    import C1Tag from '@/components/C1Components/C1Tag';

    export default {
        name: 'WorkExperience',

        components: {
            Card,
            C1Button,
            WorkExperienceModal,
            WorkExperienceModalDestroy,
            C1Tag,
        },

        filters: {
            dateFormat(value, format) {
                if (!value) {
                    return '';
                }
                return moment
                    .utc(value)
                    .local()
                    .format(format);
            },
        },

        props: {
            hideDescription: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                selectedWorkExperience: {},
                workExperienceToBeDestroyed: {},
                isModalOpen: false,
                isModalDestroyOpen: false,
            };
        },

        computed: {
            ...mapState({
                $_meWorkExperience: state => state.me.workExperience,
            }),

            ...mapGetters('site', {
                $_seekerFieldByCode: 'seekerFieldByCode',
            }),

            payBandAnnuallyFiltered() {
                const payBandAnnually = JSON.parse(
                    JSON.stringify(this.payBandAnnually),
                );
                payBandAnnually.forEach(o => {
                    if (!o.label) {
                        o.label = `$ ${numeral(o.value).format('0,0')}`;
                    }
                });

                return payBandAnnually;
            },

            payBandHourlyFiltered() {
                const payBandHourly = JSON.parse(
                    JSON.stringify(this.payBandHourly),
                );
                payBandHourly.forEach(o => {
                    if (!o.label) {
                        o.label = `$ ${numeral(o.value).format('0,0')}`;
                    }
                });

                return payBandHourly;
            },
        },

        methods: {
            getJobTypeName(jobType) {
                return has(jobType, 'value') ? jobType.value : '';
            },

            getContractTypeName(contractType) {
                return has(contractType, 'value') ? contractType.value : '';
            },

            getLocationName(location) {
                return has(location, 'label') ? location.label : '';
            },

            onAdd() {
                this.selectedWorkExperience = {};
                this.openModal();
            },

            onEdit(workExperience) {
                this.selectedWorkExperience = workExperience;
                this.openModal();
            },

            openModal() {
                this.isModalOpen = true;
            },

            closeModal() {
                this.selectedWorkExperience = {};
                this.isModalOpen = false;
            },

            onDestroy(workExperience) {
                this.workExperienceToBeDestroyed = workExperience;
                this.openModalDestroy();
            },

            openModalDestroy() {
                this.isModalDestroyOpen = true;
            },

            closeModalDestroy() {
                this.selectedWorkExperience = {};
                this.isModalDestroyOpen = false;
            },
        },
    };
</script>
